import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  User, 
  Link as LinkIcon, 
  History,
  Loader2,
  TrendingUp,
  TrendingDown,
  Search,
  BarChart3,
  Trophy,
  Target,
  Coins
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ClickableAvatar } from '../components/UserStatsModal';

export default function Profile() {
  const { user, api, refreshUser, refreshRobloxProfile } = useAuth();
  const [gameHistory, setGameHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchHistory();
    fetchStats();
    // Refresh Roblox profile data on page load
    if (user?.roblox_user_id) {
      refreshRobloxProfile();
    }
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await api.get('/games/history');
      setGameHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/users/me/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      toast.error('Please enter at least 2 characters');
      return;
    }
    
    setSearching(true);
    try {
      const response = await api.get(`/users/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data.users || []);
      if (response.data.users?.length === 0) {
        toast.info('No users found');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const formatGameType = (type) => {
    const types = {
      coinflip: 'Coinflip',
      crash: 'Crash',
      roulette: 'Roulette',
      blackjack: 'Blackjack',
      cases: 'Cases'
    };
    return types[type] || type;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-lg">
          <p className="text-slate-400 text-sm">{label}</p>
          <p className="text-green-400 font-bold">Won: R$ {payload[0]?.payload?.won?.toFixed(0) || 0}</p>
          <p className="text-red-400 font-bold">Lost: R$ {payload[0]?.payload?.lost?.toFixed(0) || 0}</p>
          <p className={`font-bold ${payload[0]?.payload?.net >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
            Net: R$ {payload[0]?.payload?.net?.toFixed(0) || 0}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <h1 className="font-display text-3xl font-bold text-white">Profile</h1>

      {/* User Info */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-6">
          {user?.roblox_avatar_url ? (
            <img
              src={user.roblox_avatar_url}
              alt="Avatar"
              className="w-24 h-24 rounded-full border-4 border-blue-500/30"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-slate-700 flex items-center justify-center">
              <User size={40} className="text-slate-400" />
            </div>
          )}
          
          <div className="flex-1">
            <h2 className="font-display text-2xl font-bold text-white">{user?.username}</h2>
            <p className="text-slate-400">{user?.email}</p>
            {user?.roblox_username && (
              <p className="text-blue-400 mt-1">Roblox: {user.roblox_username}</p>
            )}
          </div>

          {!user?.roblox_username && (
            <Link to="/app/link-roblox">
              <Button className="btn-secondary gap-2">
                <LinkIcon size={16} />
                Link Roblox
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6">
          <p className="text-slate-400 mb-2">Site Balance</p>
          <p className="font-display text-4xl font-bold text-blue-400">
            R$ {user?.site_balance?.toLocaleString() || 0}
          </p>
        </div>
        
        <div className="glass rounded-2xl p-6">
          <p className="text-slate-400 mb-2">Roblox Balance</p>
          <p className="font-display text-4xl font-bold text-green-400">
            R$ {user?.roblox_balance?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      {/* Stats Overview */}
      {statsLoading ? (
        <div className="glass rounded-2xl p-6 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
        </div>
      ) : stats && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="text-blue-400" size={18} />
                <p className="text-slate-400 text-sm">Total Games</p>
              </div>
              <p className="font-display text-2xl font-bold text-white">{stats.total_games}</p>
            </div>
            
            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Coins className="text-yellow-400" size={18} />
                <p className="text-slate-400 text-sm">Total Wagered</p>
              </div>
              <p className="font-display text-2xl font-bold text-yellow-400">R$ {stats.total_wagered?.toLocaleString()}</p>
            </div>
            
            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="text-green-400" size={18} />
                <p className="text-slate-400 text-sm">Win Rate</p>
              </div>
              <p className="font-display text-2xl font-bold text-green-400">{stats.win_rate}%</p>
            </div>
            
            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className={stats.net_profit >= 0 ? 'text-green-400' : 'text-red-400'} size={18} />
                <p className="text-slate-400 text-sm">Net Profit</p>
              </div>
              <p className={`font-display text-2xl font-bold ${stats.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {stats.net_profit >= 0 ? '+' : ''}R$ {stats.net_profit?.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Wins/Losses Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="text-green-400" size={20} />
                  <h3 className="font-display text-lg font-bold text-white">Total Won</h3>
                </div>
                <span className="text-green-400 font-bold">R$ {stats.total_won?.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-slate-700 rounded-full h-3">
                  <div 
                    className="bg-green-500 h-3 rounded-full"
                    style={{ width: `${stats.total_games > 0 ? (stats.wins / stats.total_games * 100) : 0}%` }}
                  />
                </div>
                <span className="text-slate-400 text-sm">{stats.wins} wins</span>
              </div>
            </div>
            
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <TrendingDown className="text-red-400" size={20} />
                  <h3 className="font-display text-lg font-bold text-white">Total Lost</h3>
                </div>
                <span className="text-red-400 font-bold">R$ {stats.total_lost?.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-slate-700 rounded-full h-3">
                  <div 
                    className="bg-red-500 h-3 rounded-full"
                    style={{ width: `${stats.total_games > 0 ? (stats.losses / stats.total_games * 100) : 0}%` }}
                  />
                </div>
                <span className="text-slate-400 text-sm">{stats.losses} losses</span>
              </div>
            </div>
          </div>

          {/* Profit/Loss Graph */}
          {stats.daily_data && stats.daily_data.length > 0 && (
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-6">
                <BarChart3 className="text-blue-400" size={20} />
                <h3 className="font-display text-xl font-bold text-white">Profit/Loss Over Time</h3>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.daily_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#64748b"
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      tickFormatter={(value) => value.slice(5)} // Show MM-DD
                    />
                    <YAxis 
                      stroke="#64748b"
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      tickFormatter={(value) => `R$${value}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="net" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                      activeDot={{ r: 6, fill: '#3b82f6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Stats by Game Type */}
          {stats.game_stats && Object.keys(stats.game_stats).length > 0 && (
            <div className="glass rounded-2xl p-6">
              <h3 className="font-display text-xl font-bold text-white mb-4">Stats by Game</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(stats.game_stats).map(([game, data]) => (
                  <div key={game} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                    <h4 className="font-bold text-white capitalize mb-2">{formatGameType(game)}</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Wagered:</span>
                        <span className="text-yellow-400">R$ {data.wagered?.toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Won:</span>
                        <span className="text-green-400">R$ {data.won?.toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Lost:</span>
                        <span className="text-red-400">R$ {data.lost?.toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">W/L:</span>
                        <span className="text-white">{data.wins}/{data.losses}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* User Search */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Search className="text-blue-400" size={20} />
          <h3 className="font-display text-xl font-bold text-white">Find Users</h3>
        </div>
        
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by username..."
              className="input-default"
            />
          </div>
          <Button onClick={handleSearch} disabled={searching} className="btn-primary">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
          </Button>
        </div>
        
        {searchResults.length > 0 && (
          <div className="space-y-2">
            {searchResults.map((result) => (
              <div 
                key={result.user_id} 
                className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/50 border border-slate-700/50 hover:bg-slate-800 transition-colors"
              >
                <ClickableAvatar
                  userId={result.user_id}
                  username={result.username}
                  avatarUrl={result.roblox_avatar_url}
                  size={40}
                  borderColor="border-blue-500"
                />
                <div className="flex-1">
                  <p className="font-medium text-white">{result.username}</p>
                  {result.roblox_username && (
                    <p className="text-slate-400 text-sm">Roblox: {result.roblox_username}</p>
                  )}
                </div>
                <p className="text-slate-500 text-sm">
                  Joined {new Date(result.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Game History */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <History className="text-blue-400" size={20} />
          <h3 className="font-display text-xl font-bold text-white">Recent Games</h3>
        </div>

        {historyLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
          </div>
        ) : gameHistory.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-slate-400">No games played yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {gameHistory.slice(0, 10).map((game, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700/50"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    game.won ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}>
                    {game.won ? (
                      <TrendingUp className="text-green-400" size={20} />
                    ) : (
                      <TrendingDown className="text-red-400" size={20} />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-white">{formatGameType(game.game_type)}</p>
                    <p className="text-slate-500 text-sm">
                      {new Date(game.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-mono font-bold ${game.won ? 'text-green-400' : 'text-red-400'}`}>
                    {game.won ? '+' : '-'}R$ {game.won ? game.payout?.toFixed(0) : game.bet_amount?.toFixed(0)}
                  </p>
                  <p className="text-slate-500 text-sm">Bet: R$ {game.bet_amount?.toFixed(0)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
